import { SocialLink, SocialNetwork } from "@/api/types";
import { SocialIcon } from "@/components/SocialIcon";

/**
 * The four social profiles a host can publish, as one field each.
 *
 * <p>A fixed row per network rather than an add-a-link list: there are four, the host either has one
 * or does not, and an empty field is how a link is removed. Order is fixed and matches the order the
 * invitee page renders them in.</p>
 *
 * <p>Values are sent as typed. The server accepts a handle or a full profile URL, canonicalises it,
 * and rejects a URL that is not on that network's own domains — so this component deliberately does
 * no validation of its own beyond trimming. Duplicating the rules here is how the two drift.</p>
 */

export const SOCIAL_FIELDS: {
  network: SocialNetwork;
  label: string;
  placeholder: string;
}[] = [
  { network: "LINKEDIN", label: "LinkedIn", placeholder: "your-handle" },
  { network: "INSTAGRAM", label: "Instagram", placeholder: "yourhandle" },
  { network: "X", label: "X", placeholder: "yourhandle" },
  { network: "FACEBOOK", label: "Facebook", placeholder: "yourpage" },
];

export type SocialValues = Record<SocialNetwork, string>;

/**
 * Existing links back into editable text.
 *
 * <p>A profile that is just a handle under the network's root ({@code instagram.com/scheda}) edits
 * best as the bare handle. Anything deeper — a LinkedIn {@code /in/} or {@code /company/} path — has
 * no handle form that round-trips, so it stays a full URL. Deciding by path depth rather than by
 * network keeps this from needing to know each network's URL shape, which is the server's job.</p>
 */
export function toSocialValues(links: SocialLink[] | undefined): SocialValues {
  const values = { LINKEDIN: "", INSTAGRAM: "", X: "", FACEBOOK: "" } as SocialValues;
  for (const link of links ?? []) {
    const segments = link.url.replace(/^https?:\/\/[^/]+\//, "").split("/");
    values[link.network] = segments.length === 1 ? link.label : link.url;
  }
  return values;
}

/** The PATCH shape: every network, in order, blanks included so cleared fields remove the link. */
export function toSocialRequest(values: SocialValues) {
  return SOCIAL_FIELDS.map((f) => ({ network: f.network, value: values[f.network].trim() }));
}

export function SocialLinksFields({
  values,
  onChange,
  disabled,
}: {
  values: SocialValues;
  onChange: (next: SocialValues) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {SOCIAL_FIELDS.map(({ network, label, placeholder }) => (
        <label key={network} className="flex items-center gap-2 text-sm">
          <SocialIcon network={network} className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="w-20 shrink-0 text-foreground">{label}</span>
          <input
            type="text"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            value={values[network]}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => onChange({ ...values, [network]: e.target.value })}
            className="min-w-0 flex-1 rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm disabled:opacity-60"
          />
        </label>
      ))}
    </div>
  );
}
